import React, { useState } from 'react';
import { Wallet, TrendingUp, TrendingDown, AlertCircle, Download, ArrowUpRight, ArrowDownRight, Plane, Hotel, Car, Utensils, Receipt, Users, BarChart3, ChevronRight, Clock, CheckCircle, AlertTriangle, DollarSign, PieChart, Filter } from 'lucide-react';
import { useAppContext } from '../context/AppContext';
export function BudgetPage() {
  const {
    travelRequests
  } = useAppContext();
  const [period, setPeriod] = useState('Year to Date');
  // Calculate live metrics from context
  const pendingRequests = travelRequests.filter(r => r.status === 'pending');
  const pendingCount = pendingRequests.length;
  const pendingAmount = pendingRequests.reduce((sum, req) => sum + req.amount, 0);
  // Mock Data
  const monthlySpend = [{
    month: 'Jan',
    amount: 28000,
    flight: 12000,
    hotel: 10000,
    other: 6000
  }, {
    month: 'Feb',
    amount: 32000,
    flight: 15000,
    hotel: 11000,
    other: 6000
  }, {
    month: 'Mar',
    amount: 45000,
    flight: 20000,
    hotel: 15000,
    other: 10000
  }, {
    month: 'Apr',
    amount: 38000,
    flight: 18000,
    hotel: 12000,
    other: 8000
  }, {
    month: 'May',
    amount: 42000,
    flight: 19000,
    hotel: 14000,
    other: 9000
  }, {
    month: 'Jun',
    amount: 35000,
    flight: 16000,
    hotel: 11000,
    other: 8000
  }, {
    month: 'Jul',
    amount: 29000,
    flight: 13000,
    hotel: 10000,
    other: 6000
  }, {
    month: 'Aug',
    amount: 48000,
    flight: 22000,
    hotel: 16000,
    other: 10000
  }, {
    month: 'Sep',
    amount: 31000,
    flight: 14000,
    hotel: 10000,
    other: 7000
  }, {
    month: 'Oct',
    amount: 34000,
    flight: 15000,
    hotel: 11000,
    other: 8000
  }, {
    month: 'Nov',
    amount: 0,
    flight: 0,
    hotel: 0,
    other: 0
  }, {
    month: 'Dec',
    amount: 0,
    flight: 0,
    hotel: 0,
    other: 0
  }];
  const categories = [{
    name: 'Flights',
    amount: 156000,
    percent: 43,
    icon: Plane,
    color: 'bg-blue-500',
    text: 'text-blue-600',
    bg: 'bg-blue-50'
  }, {
    name: 'Hotels',
    amount: 98000,
    percent: 27,
    icon: Hotel,
    color: 'bg-indigo-500',
    text: 'text-indigo-600',
    bg: 'bg-indigo-50'
  }, {
    name: 'Ground Transport',
    amount: 52000,
    percent: 14,
    icon: Car,
    color: 'bg-emerald-500',
    text: 'text-emerald-600',
    bg: 'bg-emerald-50'
  }, {
    name: 'Meals & Entertainment',
    amount: 38000,
    percent: 11,
    icon: Utensils,
    color: 'bg-amber-500',
    text: 'text-amber-600',
    bg: 'bg-amber-50'
  }, {
    name: 'Other',
    amount: 18000,
    percent: 5,
    icon: Receipt,
    color: 'bg-slate-400',
    text: 'text-slate-600',
    bg: 'bg-slate-100'
  }];
  const topSpenders = [{
    name: 'Sarah Jenkins',
    dept: 'Sales',
    amount: 24500,
    initials: 'SJ',
    color: 'bg-purple-100 text-purple-700'
  }, {
    name: 'David Miller',
    dept: 'Engineering',
    amount: 18200,
    initials: 'DM',
    color: 'bg-blue-100 text-blue-700'
  }, {
    name: 'Michael Chen',
    dept: 'Product',
    amount: 15800,
    initials: 'MC',
    color: 'bg-amber-100 text-amber-700'
  }, {
    name: 'Lisa Park',
    dept: 'Marketing',
    amount: 12400,
    initials: 'LP',
    color: 'bg-emerald-100 text-emerald-700'
  }, {
    name: 'James Wilson',
    dept: 'Engineering',
    amount: 11900,
    initials: 'JW',
    color: 'bg-blue-100 text-blue-700'
  }];
  const departments = [{
    name: 'Engineering',
    budget: 150000,
    spent: 85000,
    remaining: 65000,
    percent: 56,
    status: 'On Track',
    color: 'emerald'
  }, {
    name: 'Sales',
    budget: 200000,
    spent: 145000,
    remaining: 55000,
    percent: 72,
    status: 'Warning',
    color: 'amber'
  }, {
    name: 'Marketing',
    budget: 100000,
    spent: 45000,
    remaining: 55000,
    percent: 45,
    status: 'On Track',
    color: 'emerald'
  }, {
    name: 'Product',
    budget: 80000,
    spent: 75000,
    remaining: 5000,
    percent: 93,
    status: 'Critical',
    color: 'red'
  }, {
    name: 'HR',
    budget: 50000,
    spent: 12000,
    remaining: 38000,
    percent: 24,
    status: 'On Track',
    color: 'emerald'
  }];
  const alerts = [{
    type: 'critical',
    title: 'Product department at 93% utilization',
    desc: 'Only $5,000 remaining for Q4. Immediate review required.',
    time: '2 hours ago',
    icon: AlertCircle,
    color: 'border-l-4 border-red-500 bg-red-50 text-red-800'
  }, {
    type: 'warning',
    title: 'Sales department approaching 75% threshold',
    desc: 'Review upcoming travel plans for Q4 to ensure compliance.',
    time: '1 day ago',
    icon: AlertTriangle,
    color: 'border-l-4 border-amber-500 bg-amber-50 text-amber-800'
  }, {
    type: 'info',
    title: `${pendingCount} pending requests totaling $${pendingAmount.toLocaleString()}`,
    desc: 'Awaiting approval from department heads.',
    time: 'Just now',
    icon: Clock,
    color: 'border-l-4 border-blue-500 bg-blue-50 text-blue-800'
  }, {
    type: 'success',
    title: 'HR department under budget by 76%',
    desc: 'Consider budget reallocation to high-utilization departments.',
    time: '3 days ago',
    icon: CheckCircle,
    color: 'border-l-4 border-emerald-500 bg-emerald-50 text-emerald-800'
  }];
  return <div className="space-y-6 pb-12" data-id="element-499">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-500">
        <div data-id="element-501">
          <h2 className="text-2xl font-bold text-slate-800" data-id="element-502">
            Budget Management
          </h2>
          <p className="text-slate-500" data-id="element-503">
            Track and control corporate travel spend across departments.
          </p>
        </div>
        <div className="flex items-center gap-3" data-id="element-504">
          <div className="relative" data-id="element-505">
            <select value={period} onChange={e => setPeriod(e.target.value)} className="appearance-none rounded-lg border border-slate-300 bg-white px-4 py-2 pr-8 text-sm font-medium text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-506">
              <option data-id="element-507">This Quarter</option>
              <option data-id="element-508">Last Quarter</option>
              <option data-id="element-509">Year to Date</option>
              <option data-id="element-510">Full Year</option>
            </select>
            <Filter className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400 pointer-events-none" data-id="element-511" />
          </div>
          <button className="flex items-center rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50" data-id="element-512">
            <Download className="mr-2 h-4 w-4" data-id="element-513" />
            Export Report
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4" data-id="element-514">
        {/* Annual Budget */}
        <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm relative overflow-hidden" data-id="element-515">
          <div className="flex justify-between items-start mb-4" data-id="element-516">
            <div data-id="element-517">
              <p className="text-sm font-medium text-slate-500" data-id="element-518">
                Total Annual Budget
              </p>
              <h3 className="text-2xl font-bold text-slate-900 mt-1" data-id="element-519">
                $580,000
              </h3>
            </div>
            <div className="h-10 w-10 rounded-full bg-blue-50 flex items-center justify-center text-blue-600" data-id="element-520">
              <Wallet className="h-5 w-5" data-id="element-521" />
            </div>
          </div>
          <div className="flex items-center gap-2" data-id="element-522">
            <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden" data-id="element-523">
              <div className="h-full bg-blue-600 rounded-full" style={{
              width: '62%'
            }} data-id="element-524"></div>
            </div>
            <span className="text-sm font-medium text-slate-700" data-id="element-525">62%</span>
          </div>
          <p className="text-xs text-slate-500 mt-2" data-id="element-526">Utilized</p>
        </div>

        {/* Spent YTD */}
        <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm" data-id="element-527">
          <div className="flex justify-between items-start mb-4" data-id="element-528">
            <div data-id="element-529">
              <p className="text-sm font-medium text-slate-500" data-id="element-530">
                Spent Year-to-Date
              </p>
              <h3 className="text-2xl font-bold text-slate-900 mt-1" data-id="element-531">
                $362,000
              </h3>
            </div>
            <div className="h-10 w-10 rounded-full bg-emerald-50 flex items-center justify-center text-emerald-600" data-id="element-532">
              <TrendingUp className="h-5 w-5" data-id="element-533" />
            </div>
          </div>
          <div className="flex items-center text-sm font-medium text-emerald-600" data-id="element-534">
            <ArrowUpRight className="mr-1 h-4 w-4" data-id="element-535" />
            +12% vs last year
          </div>
        </div>

        {/* Remaining */}
        <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm" data-id="element-536">
          <div className="flex justify-between items-start mb-4" data-id="element-537">
            <div data-id="element-538">
              <p className="text-sm font-medium text-slate-500" data-id="element-539">
                Remaining Budget
              </p>
              <h3 className="text-2xl font-bold text-slate-900 mt-1" data-id="element-540">
                $218,000
              </h3>
            </div>
            <div className="h-10 w-10 rounded-full bg-indigo-50 flex items-center justify-center text-indigo-600" data-id="element-541">
              <PieChart className="h-5 w-5" data-id="element-542" />
            </div>
          </div>
          <p className="text-sm text-slate-500" data-id="element-543">Avg $72.7k/month remaining</p>
        </div>

        {/* Pending Requests */}
        <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm" data-id="element-544">
          <div className="flex justify-between items-start mb-4" data-id="element-545">
            <div data-id="element-546">
              <p className="text-sm font-medium text-slate-500" data-id="element-547">
                Pending Requests
              </p>
              <h3 className="text-2xl font-bold text-slate-900 mt-1" data-id="element-548">
                ${pendingAmount.toLocaleString()}
              </h3>
            </div>
            <div className="h-10 w-10 rounded-full bg-amber-50 flex items-center justify-center text-amber-600" data-id="element-549">
              <Clock className="h-5 w-5" data-id="element-550" />
            </div>
          </div>
          <div className="flex items-center text-sm font-medium text-amber-600" data-id="element-551">
            <AlertCircle className="mr-1 h-4 w-4" data-id="element-552" />
            {pendingCount} requests awaiting approval
          </div>
        </div>
      </div>

      {/* Monthly Spend Trend */}
      <div className="rounded-xl border border-slate-200 bg-white shadow-sm" data-id="element-553">
        <div className="border-b border-slate-200 px-6 py-4 flex items-center justify-between" data-id="element-554">
          <h3 className="font-semibold text-slate-800" data-id="element-555">Monthly Travel Spend</h3>
          <div className="flex items-center gap-4 text-sm" data-id="element-556">
            <div className="flex items-center gap-1.5" data-id="element-557">
              <div className="w-3 h-3 rounded-full bg-blue-500" data-id="element-558"></div>
              <span className="text-slate-600" data-id="element-559">Flights</span>
            </div>
            <div className="flex items-center gap-1.5" data-id="element-560">
              <div className="w-3 h-3 rounded-full bg-indigo-500" data-id="element-561"></div>
              <span className="text-slate-600" data-id="element-562">Hotels</span>
            </div>
            <div className="flex items-center gap-1.5" data-id="element-563">
              <div className="w-3 h-3 rounded-full bg-slate-300" data-id="element-564"></div>
              <span className="text-slate-600" data-id="element-565">Other</span>
            </div>
          </div>
        </div>
        <div className="p-6" data-id="element-566">
          <div className="h-64 flex items-end justify-between gap-2" data-id="element-567">
            {monthlySpend.map((data, index) => {
            const max = 50000; // Max scale
            const height = data.amount / max * 100;
            const flightH = data.flight / data.amount * 100;
            const hotelH = data.hotel / data.amount * 100;
            const otherH = data.other / data.amount * 100;
            return <div key={index} className="flex-1 flex flex-col items-center gap-2 group" data-id="element-568">
                  <div className="w-full max-w-[40px] bg-slate-100 rounded-t-sm relative flex flex-col-reverse overflow-hidden transition-all hover:opacity-90" style={{
                height: `${height}%`
              }} data-id="element-569">
                    {data.amount > 0 && <>
                        <div className="bg-blue-500 w-full" style={{
                    height: `${flightH}%`
                  }} data-id="element-570"></div>
                        <div className="bg-indigo-500 w-full" style={{
                    height: `${hotelH}%`
                  }} data-id="element-571"></div>
                        <div className="bg-slate-300 w-full" style={{
                    height: `${otherH}%`
                  }} data-id="element-572"></div>
                      </>}
                  </div>
                  <span className="text-xs font-medium text-slate-500" data-id="element-573">
                    {data.month}
                  </span>
                  {/* Tooltip on hover could go here */}
                </div>;
          })}
          </div>
        </div>
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6" data-id="element-574">
        {/* Category Breakdown */}
        <div className="lg:col-span-3 rounded-xl border border-slate-200 bg-white shadow-sm" data-id="element-575">
          <div className="border-b border-slate-200 px-6 py-4" data-id="element-576">
            <h3 className="font-semibold text-slate-800" data-id="element-577">Spend by Category</h3>
          </div>
          <div className="p-6 space-y-6" data-id="element-578">
            {categories.map(cat => <div key={cat.name} data-id="element-579">
                <div className="flex items-center justify-between mb-2" data-id="element-580">
                  <div className="flex items-center gap-3" data-id="element-581">
                    <div className={`p-2 rounded-lg ${cat.bg} ${cat.text}`} data-id="element-582">
                      <cat.icon className="h-4 w-4" data-id="element-583" />
                    </div>
                    <span className="font-medium text-slate-700" data-id="element-584">
                      {cat.name}
                    </span>
                  </div>
                  <div className="text-right" data-id="element-585">
                    <span className="font-bold text-slate-900" data-id="element-586">
                      ${cat.amount.toLocaleString()}
                    </span>
                    <span className="text-sm text-slate-500 ml-2" data-id="element-587">
                      ({cat.percent}%)
                    </span>
                  </div>
                </div>
                <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden" data-id="element-588">
                  <div className={`h-full ${cat.color} rounded-full`} style={{
                width: `${cat.percent}%`
              }} data-id="element-589"></div>
                </div>
              </div>)}
          </div>
        </div>

        {/* Top Spenders */}
        <div className="lg:col-span-2 rounded-xl border border-slate-200 bg-white shadow-sm" data-id="element-590">
          <div className="border-b border-slate-200 px-6 py-4 flex justify-between items-center" data-id="element-591">
            <h3 className="font-semibold text-slate-800" data-id="element-592">Top Travelers</h3>
            <button className="text-sm text-blue-600 font-medium hover:underline" data-id="element-593">
              View All
            </button>
          </div>
          <div className="divide-y divide-slate-100" data-id="element-594">
            {topSpenders.map((person, i) => <div key={i} className="p-4 flex items-center justify-between hover:bg-slate-50 transition-colors" data-id="element-595">
                <div className="flex items-center gap-3" data-id="element-596">
                  <div className={`h-10 w-10 rounded-full flex items-center justify-center text-sm font-bold ${person.color}`} data-id="element-597">
                    {person.initials}
                  </div>
                  <div data-id="element-598">
                    <p className="font-medium text-slate-900" data-id="element-599">{person.name}</p>
                    <p className="text-xs text-slate-500" data-id="element-600">{person.dept}</p>
                  </div>
                </div>
                <span className="font-bold text-slate-900" data-id="element-601">
                  ${person.amount.toLocaleString()}
                </span>
              </div>)}
          </div>
        </div>
      </div>

      {/* Department Utilization */}
      <div className="rounded-xl border border-slate-200 bg-white shadow-sm" data-id="element-602">
        <div className="border-b border-slate-200 px-6 py-4 flex justify-between items-center" data-id="element-603">
          <div data-id="element-604">
            <h3 className="font-semibold text-slate-800" data-id="element-605">
              Department Budget Utilization
            </h3>
            <p className="text-xs text-slate-500 mt-1" data-id="element-606">
              2 departments exceeding 75% threshold
            </p>
          </div>
          <button className="text-sm text-blue-600 font-medium hover:underline" data-id="element-607">
            Manage Allocations
          </button>
        </div>
        <div className="overflow-x-auto" data-id="element-608">
          <table className="w-full text-left text-sm" data-id="element-609">
            <thead className="bg-slate-50 text-slate-500" data-id="element-610">
              <tr data-id="element-611">
                <th className="px-6 py-3 font-medium" data-id="element-612">Department</th>
                <th className="px-6 py-3 font-medium" data-id="element-613">Allocated</th>
                <th className="px-6 py-3 font-medium" data-id="element-614">Spent</th>
                <th className="px-6 py-3 font-medium" data-id="element-615">Remaining</th>
                <th className="px-6 py-3 font-medium" data-id="element-616">Utilization</th>
                <th className="px-6 py-3 font-medium" data-id="element-617">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200" data-id="element-618">
              {departments.map(dept => <tr key={dept.name} className="hover:bg-slate-50" data-id="element-619">
                  <td className="px-6 py-4 font-medium text-slate-900" data-id="element-620">
                    {dept.name}
                  </td>
                  <td className="px-6 py-4 text-slate-600" data-id="element-621">
                    ${dept.budget.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 text-slate-600" data-id="element-622">
                    ${dept.spent.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 text-slate-600" data-id="element-623">
                    ${dept.remaining.toLocaleString()}
                  </td>
                  <td className="px-6 py-4" data-id="element-624">
                    <div className="flex items-center gap-2" data-id="element-625">
                      <span className="text-slate-700 w-8" data-id="element-626">
                        {dept.percent}%
                      </span>
                      <div className="h-2 w-24 bg-slate-100 rounded-full overflow-hidden" data-id="element-627">
                        <div className={`h-full rounded-full ${dept.color === 'emerald' ? 'bg-emerald-500' : dept.color === 'amber' ? 'bg-amber-500' : 'bg-red-500'}`} style={{
                      width: `${dept.percent}%`
                    }} data-id="element-628"></div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-629">
                    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${dept.status === 'On Track' ? 'bg-emerald-100 text-emerald-800' : dept.status === 'Warning' ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'}`} data-id="element-630">
                      {dept.status === 'Critical' && <AlertCircle className="mr-1 h-3 w-3" data-id="element-631" />}
                      {dept.status}
                    </span>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>

      {/* Budget Alerts */}
      <div className="rounded-xl border border-slate-200 bg-white shadow-sm" data-id="element-632">
        <div className="border-b border-slate-200 px-6 py-4 flex items-center gap-2" data-id="element-633">
          <h3 className="font-semibold text-slate-800" data-id="element-634">Budget Alerts</h3>
          <span className="bg-red-100 text-red-700 text-xs font-bold px-2 py-0.5 rounded-full" data-id="element-635">
            3
          </span>
        </div>
        <div className="divide-y divide-slate-100" data-id="element-636">
          {alerts.map((alert, i) => <div key={i} className={`p-4 flex items-start gap-4 hover:bg-slate-50 transition-colors ${alert.color}`} data-id="element-637">
              <div className="mt-0.5" data-id="element-638">
                <alert.icon className="h-5 w-5" data-id="element-639" />
              </div>
              <div className="flex-1" data-id="element-640">
                <h4 className="font-semibold text-sm" data-id="element-641">{alert.title}</h4>
                <p className="text-sm opacity-90 mt-0.5" data-id="element-642">{alert.desc}</p>
                <span className="text-xs opacity-75 mt-2 block" data-id="element-643">
                  {alert.time}
                </span>
              </div>
              <button className="text-sm font-medium underline opacity-80 hover:opacity-100" data-id="element-644">
                View Details
              </button>
            </div>)}
        </div>
      </div>
    </div>;
}